import { AppText } from "@toothfairy/shared-ui";
import AppChoice from "@toothfairy/shared-ui/AppChoice";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppInputLabel from "@toothfairy/shared-ui/AppInputLabel";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppTrackSlider from "@toothfairy/shared-ui/AppTrackSlider";
import AppWeightDistribution from "@toothfairy/shared-ui/AppWeightDistribution";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../MainViewContainer";
import { useModels } from "../../Common/baseModels";

const MetaSelection = ({
  selectedType,
  setSelectedType,
  batchTypes,
  setIsBatchInitiationModalOpen,
  batchConfig,
  setBatchConfig,
  topics,
  handleBatchInitiation,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const {
    functionCallingModels: _functionCallingModels,
    isLoading: isModelsLoading,
  } = useModels();

  const { state } = AppStateManager.useAppStateManager();
  const [finetunedModels, setFinetunedModels] = useState([]);
  const { getActiveWorkspace, getFinetunedModels } = useWorkspaces();
  useEffect(() => {
    setFinetunedModels(getFinetunedModels());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.subscriptionType]);
  const handleWeightsChange = (weights) => {
    setBatchConfig({
      ...batchConfig,
      weights,
    });
    // Do something with the updated weights
  };

  const _reviewModels = [
    ..._functionCallingModels?.filter((u) => u?.plannerAvailable),
    ...finetunedModels,
  ];

  const _inputFields = useMemo(() => {
    return (
      <>
        <AppInputLabel
          style={{ marginLeft: 0, marginBottom: 5 }}
          label={t("batch.meta.selectType", { defaultValue: "Benchmark type" })}
        />
        <AppDropDown
          dropDownStyle={{
            width: "100%",
            borderColor: currentTheme?.theme?.border_color,
            marginBottom: 20,
          }}
          isElevated={false}
          dropdownIconColor="transparent"
          items={(batchTypes || Constants.BATCH_TYPES)?.map((bt) => ({
            id: bt.key,
            label: `${bt.short} — ${bt.label}`,
          }))}
          propertyLabel="label"
          propertyValue="id"
          selectedValue={selectedType}
          onValueChange={(v) => setSelectedType(v)}
        />

        {(selectedType === "bm" || selectedType === "abm") && (
          <AppDropDown
            withEmptyItem
            showTextInputLabel={true}
            label={
              selectedType === "abm"
                ? t("batch.meta.benchmarkedAgent")
                : t("batch.meta.agent")
            }
            inputLabelStyle={{
              marginLeft: 0,
              marginRight: 10,
              marginTop: 0,
              marginBottom: 5,
            }}
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              width: "100%",
            }}
            wrapperContainer={{
              marginBottom: 20,
              width: "100%",
              alignSelf: "stretch",
            }}
            items={[
              {
                type: "noSelection",
                label: t("batch.meta.selectAgent"),
              },
              ...state?.agents?.filter((u) => {
                return u?.mode !== "voice" && u?.mode !== "computer";
              }),
            ]}
            propertyLabel="label"
            propertyValue="id"
            onValueChange={(value) => {
              setBatchConfig({
                ...batchConfig,
                agentId: value,
              });
            }}
          />
        )}

        <AppTextInput
          placeholder={t("batch.meta.namePlaceholder")}
          style={{
            marginTop: 2,
          }}
          wrapperInputStyle={{
            width: "100%",
            alignSelf: "stretch",
            marginBottom: 20,
          }}
          textContainer={{
            margin: 0,
          }}
          type="text"
          value={batchConfig?.name}
          onChangeText={(text) => {
            setBatchConfig({ ...batchConfig, name: text });
          }}
        />
        <AppTextInput
          multiline={true}
          numberOfLines={4}
          placeholder={t("batch.meta.descrPlaceholder")}
          style={{
            marginTop: 2,
          }}
          wrapperInputStyle={{
            width: "100%",
            alignSelf: "stretch",
            marginBottom: 20,
          }}
          textContainer={{
            margin: 0,
            padding: 10,
            marginHorizontal: 0,
            minHeight: 150,
          }}
          type="text"
          value={batchConfig?.description}
          onChangeText={(text) => {
            setBatchConfig({ ...batchConfig, description: text });
          }}
        />
        {(selectedType == "bm" || selectedType == "abm") && (
          <AppDropDown
            showTextInputLabel={true}
            label={t("batch.meta.benchmark")}
            inputLabelStyle={{
              marginLeft: 0,
              marginRight: 10,
              marginTop: 0,
              marginBottom: 5,
            }}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              width: "100%",
            }}
            wrapperContainer={{
              marginBottom: 15,
              width: "100%",
              alignSelf: "stretch",
            }}
            isElevated={false}
            dropdownIconColor="transparent"
            items={[
              { id: "none", name: t("batch.meta.selectBenchmark") },
              ...state?.benchmarks,
            ]}
            propertyLabel="name"
            propertyValue="id"
            selectedValue={batchConfig?.benchmarkID}
            onValueChange={(itemValue, itemIndex) => {
              setBatchConfig({
                ...batchConfig,
                benchmarkID: itemValue,
              });
            }}
          />
        )}

        {selectedType === "abm" &&
          batchConfig?.benchmarkID &&
          batchConfig?.benchmarkID !== "none" && (
            <AppText
              style={{
                fontSize: currentTheme?.theme?.extra_small_font_size,
                color: currentTheme?.theme?.darkGrey,
                marginTop: 10,
                marginBottom: 20,
              }}
            >
              {t("batch.meta.reviewerModelDescription")}
            </AppText>
          )}

        {(selectedType === "bm" || selectedType === "abm") && (
          <>
            <AppText
              style={{
                fontWeight: currentTheme?.theme?.font_bold,
                fontSize: currentTheme?.theme?.small_font_size,
                marginBottom: 5,
              }}
            >
              {t("batch.meta.evaluationCriteria")}
            </AppText>
            <AppWeightDistribution
              containerStyle={{
                paddingVertical: 10,
              }}
              onWeightsChange={handleWeightsChange}
              initialWeights={batchConfig?.weights}
            />
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                marginBottom: 5,
              }}
            >
              {t("batch.meta.passingThreshold")}
            </AppText>
            <AppTrackSlider
              step={0.1}
              maximumValue={100}
              minimumValue={50}
              unitLabel={"%"}
              value={batchConfig?.passingThreashold || 90}
              onValueChange={(v) => {
                setBatchConfig({
                  ...batchConfig,
                  passingThreashold: v,
                });
              }}
              wrapperStyle={{
                marginBottom: 20,
              }}
            />
          </>
        )}
      </>
    );
  }, [
    batchConfig?.name,
    batchConfig?.description,
    batchConfig?.mode,
    batchConfig?.maxTurns,
    batchConfig?.examinerAgentId,
    batchConfig?.consistencyThreshold,
    batchConfig?.benchmarkID,
    setBatchConfig,
    JSON.stringify(batchConfig),
    JSON.stringify(state?.agents),
    JSON.stringify(state?.benchmarks),
    selectedType,
  ]);

  return (
    <AppChoice
      wrapperStyle={{ alignSelf: "center", width: 600 }}
      caption={``}
      subCaption={t("batch.modal.subCaption")}
      cancelCaption={t("training.modals.cancelCaption")}
      confirmCaption={t("training.modals.confirmCaption")}
      onConfirmButtonPress={() => {
        if (
          (batchConfig?.agentId == null ||
            batchConfig?.agentId === "noSelection" ||
            batchConfig?.agentId == "[object Object]") &&
          (selectedType === "bm" || selectedType === "abm")
        ) {
          AppToasty.show(t("batch.meta.toast.selectAgent"), {
            placement: "bottom",
            type: "danger",
          });
          return;
        }
        if (selectedType === "abm" || selectedType === "bm") {
          if (!batchConfig.name || batchConfig?.name?.length === 0) {
            return AppToasty.show(t("batch.meta.toast.enterName"), {
              type: "danger",
            });
          }
          if (!batchConfig.agentId) {
            return AppToasty.show(t("batch.meta.toast.selectAgentForRun"), {
              type: "danger",
            });
          }
          if (!batchConfig.reviewerAgentID) {
            return AppToasty.show(t("batch.meta.toast.selectReviewerModel"), {
              type: "danger",
            });
          }
          if (batchConfig.benchmarkID == "none" || !batchConfig.benchmarkID) {
            return AppToasty.show(t("batch.meta.toast.selectBenchmark"), {
              type: "danger",
            });
          }
          if (selectedType === "abm") {
            if (
              !batchConfig.examinerAgentId ||
              batchConfig.examinerAgentId === "noSelection"
            ) {
              return AppToasty.show(t("batch.meta.toast.selectExaminerAgent"), {
                type: "danger",
              });
            }
            if (!batchConfig.maxTurns || batchConfig.maxTurns < 1) {
              return AppToasty.show(t("batch.meta.toast.setMaxTurns"), {
                type: "danger",
              });
            }
          }
          handleBatchInitiation(batchConfig);
          setIsBatchInitiationModalOpen(false);
        } else {
          AppToasty.show(t("batchParsingCreationFailedCaption"), {
            placement: "bottom",
            type: "danger",
          });
        }
      }}
      onCancelButtonPress={() => {
        setIsBatchInitiationModalOpen(false);
      }}
      AppChoiceButton={ToothFairyButton}
    >
      <View
        style={{
          flexDirection: "column",
          marginTop: 20,
          width: "100%",
          alignSelf: "stretch",
        }}
      >
        {_inputFields}
      </View>
    </AppChoice>
  );
};

export default MetaSelection;
